import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fireEvent, startLifeguideHandoff } from "@/lib/api";
import { track } from "@/lib/analytics";
import { WisdomFinder } from "@/components/WisdomFinder";
import { toast } from "sonner";
import { Search, Heart, Send, ArrowRight, BookOpen, Check, MessageSquareText, HeartHandshake, Gift, Sparkles, X, Smartphone } from "lucide-react";
import { CardShowcase } from "@/components/CardShowcase";
import { usePwa } from "@/context/PwaContext";
import logo from "@/assets/proverb-logo.webp";

const CONCERN_WORDS = ["Anxiety", "Marriage", "Money", "Family", "Work", "Grief", "Decisions", "Relationships"];
const NEED_WORDS = ["Peace", "Courage", "Hope", "Direction", "Comfort", "Wisdom"];

export default function Home() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [cardModal, setCardModal] = useState(false);
  const pwa = usePwa();

  useEffect(() => {
    try { if (!localStorage.getItem("ipf_visited")) { localStorage.setItem("ipf_visited", "1"); fireEvent("visit"); } } catch {}
    import("@/lib/api").then(({ api }) => api.get("/library-stats").then((r) => setStats(r.data)).catch(() => {}));
  }, []);

  const scrollToFind = () => { const el = document.getElementById("find"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const total = stats ? `${(Math.floor(stats.total / 100) * 100).toLocaleString()}+` : "3,400+";
  const provCount = stats ? stats.proverbs : 916;
  const statVal = (v, fallback) => Number(v ?? fallback).toLocaleString();

  const lifeguide = async () => {
    track("bridge_click", { path: "/", source: "home" });
    try { const r = await startLifeguideHandoff({ topic: "", context: "home" }); if (!r.ok) toast.error("Could not open LifeGuide right now. Please try again."); }
    catch { toast.error("Could not open LifeGuide right now. Please try again."); }
  };

  const sayItBetter = async () => {
    track("bridge_click", { path: "/", source: "say_it_better" });
    try { const r = await startLifeguideHandoff({ topic: "", context: "say_it_better", feature: "say-it-better" }); if (!r.ok) toast.error("Could not open LifeGuide right now. Please try again."); }
    catch { toast.error("Could not open LifeGuide right now. Please try again."); }
  };

  return (
    <div>
      {/* Brand hero */}
      <section className="relative overflow-hidden paper-texture">
        <div className="absolute inset-0 -z-10">
          <img src="/assets/home-cards.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#FDFBF7]/94" />
        </div>
        <div className="max-w-4xl lg:max-w-[1180px] mx-auto px-5 sm:px-8 pt-14 lg:pt-16 pb-6 text-center">
          <img data-testid="home-brand-logo" src={logo} alt="I Need a Proverb" className="mx-auto h-16 sm:h-20 lg:h-24 w-auto" />
          <p className="mt-3 font-serif-display text-2xl sm:text-3xl tracking-[0.06em] text-[#0F172A] uppercase">I Need a Proverb</p>
          <span data-testid="free-badge" className="inline-block text-xs font-semibold tracking-wide uppercase text-[#2C4C3B] bg-[#EAF1EA] border border-[#CFE0CF] rounded-full px-4 py-1.5 mt-4 mb-6">Free to use</span>
          <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.03] text-[#0F172A]">Whatever you're facing,<br className="hidden sm:block" /> there's wisdom for it.</h1>
          <p className="mt-5 text-lg lg:text-xl text-[#475569] max-w-2xl mx-auto leading-relaxed">Find, receive, save, and share the right biblical wisdom for what you're going through.</p>
          <button data-testid="home-save-phone" onClick={() => pwa?.openInstall()}
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#2C4C3B] text-[#2C4C3B] bg-white/70 px-5 py-2.5 text-sm font-semibold hover:bg-[#EAF1EA] transition-colors">
            <Smartphone className="w-4 h-4" strokeWidth={1.9} /> Save I Need a Proverb to Your Phone
          </button>
        </div>
      </section>

      {/* Three primary choices */}
      <section className="max-w-6xl lg:max-w-[1180px] mx-auto px-5 sm:px-8 pb-4">
        <div className="grid sm:grid-cols-3 gap-4">
          <Choice testid="choice-find" icon={Search} title="Browse by Topic" sub="Explore Scripture by topic or concern." onClick={scrollToFind} primary />
          <Choice testid="choice-daily" icon={Heart} title="Get Personal Guidance" sub="Tell us what's happening and get Scripture matched to your situation." onClick={() => nav("/guidance")} />
          <Choice testid="choice-send" icon={Send} title="Send Wisdom" sub="Encourage someone you care about." onClick={() => nav("/send")} />
        </div>
      </section>

      {/* Sponsor Program — subtle callout, secondary to Find Wisdom */}
      <section data-testid="sponsor-callout-section" className="max-w-4xl mx-auto px-5 sm:px-8 pt-2 pb-2">
        <div className="rounded-2xl border border-[#E7DFC9] bg-[#FBF7EE] px-6 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#EAF1EA] shrink-0"><HeartHandshake className="w-5 h-5 text-[#2C4C3B]" strokeWidth={1.8} /></span>
          <p className="flex-1 text-sm text-[#475569] leading-relaxed">
            Know a church, ministry, Christian school, business, or organization that would like to share biblical wisdom? <span className="text-[#0F172A] font-medium">I Need a Proverb offers a Sponsor Program with custom Sharing Cards, branded QR codes, and sponsor tools.</span>
          </p>
          <button data-testid="sponsor-learn-btn" onClick={() => nav("/sponsor")}
            className="shrink-0 rounded-full border border-[#2C4C3B] text-[#2C4C3B] px-5 py-2.5 text-sm font-semibold hover:bg-[#F1F5F1] transition-colors whitespace-nowrap">
            Learn About Sponsorship
          </button>
        </div>
      </section>

      {/* Scripture depth social proof */}
      <section data-testid="depth-section" className="max-w-4xl mx-auto px-5 sm:px-8 py-12 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#B8912F] font-semibold mb-3">Scripture that fits your moment</p>
        <h2 className="font-serif-display text-3xl sm:text-4xl text-[#0F172A]">Biblical wisdom that's personal to what you're facing.</h2>
        <p className="mt-4 text-[#475569] leading-relaxed max-w-2xl mx-auto">
          Explore more than {total} Scripture passages from Proverbs, Psalms, and carefully selected wisdom throughout the Bible — matched to the real-life concerns you're facing.
        </p>
        <p className="mt-4 font-serif-display text-xl text-[#2C4C3B]">Thousands of Scriptures. Countless ways to find wisdom for your moment.</p>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-3xl mx-auto">
          <Stat value={statVal(stats?.proverbs, 916)} label="Proverbs verses" />
          <Stat value={statVal(stats?.psalms, 2461)} label="Psalm verses" />
          <Stat value={`${statVal(stats?.total, 3464)}+`} label="Total Scripture passages" />
          <Stat value={statVal(stats?.topics, 23)} label="Real-life concerns" />
        </div>
        <p className="mt-6 text-sm font-semibold text-[#2C4C3B]">I Need a Proverb is always free.</p>
      </section>

      {/* Finder */}
      <section id="find" className="max-w-3xl mx-auto px-5 sm:px-8 pt-4 pb-16 scroll-mt-24">
        <div className="text-center mb-6">
          <h2 className="font-serif-display text-3xl sm:text-4xl tracking-tight text-[#0F172A]">Find wisdom for today</h2>
          <p className="text-[#475569] mt-2">Take a breath — this only takes a moment.</p>
        </div>
        <WisdomFinder />
      </section>

      {/* LifeGuide bridge (below the free experience; IANP stays the hero) */}
      <section data-testid="home-lifeguide-section" className="max-w-5xl mx-auto px-5 sm:px-8 py-16">
        <div className="rounded-3xl bg-[#0F172A] text-white px-7 sm:px-12 py-12 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#C9A44C]/10 blur-3xl" />
          <div className="relative">
            <p className="text-xs uppercase tracking-[0.25em] text-[#C9A44C] font-semibold mb-3">Need more than a verse?</p>
            <h3 className="font-serif-display text-3xl sm:text-4xl leading-tight">Sometimes finding the right Scripture is the beginning.</h3>
            <p className="mt-4 text-white/75 leading-relaxed max-w-2xl">
              <strong className="text-white">I Need a Proverb</strong> helps you <em className="text-[#E7CF8f] not-italic font-semibold">find</em> the right biblical wisdom. <strong className="text-white">LifeGuide</strong> helps you <em className="text-[#E7CF8f] not-italic font-semibold">apply</em> biblical wisdom to what you're facing.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-8">
              <BenefitGroup title="Guidance & next steps" items={[
                "Think through what you're really facing",
                "Get practical next steps for your situation",
                "Understand different ways to approach a problem",
                "Work through difficult decisions",
                "Continue the conversation over time instead of starting over",
              ]} />
              <BenefitGroup title="Communication & reflection" items={[
                "Explore a deeper biblical perspective",
                "Get help communicating with a spouse, family member, friend, or coworker",
                "Reflect more deeply on what you're learning",
                "Save thoughts and insights in your personal journal",
                "Receive personalized guidance for the situation you're actually facing",
              ]} />
            </div>

            {/* Say It Better callout — clickable, routes into the existing LifeGuide flow */}
            <button data-testid="say-it-better-callout" onClick={sayItBetter} type="button"
              className="mt-8 w-full text-left rounded-2xl border border-[#C9A44C]/30 bg-[#C9A44C]/10 px-6 py-5 flex items-start gap-4 hover:bg-[#C9A44C]/15 hover:border-[#C9A44C]/50 transition-colors group cursor-pointer">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#C9A44C]/20 shrink-0"><MessageSquareText className="w-5 h-5 text-[#E7CF8f]" strokeWidth={1.8} /></span>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-[0.2em] text-[#E7CF8f] font-semibold">Say It Better</p>
                <p className="mt-1 text-sm text-white/85 leading-relaxed">Before you send that difficult text, let LifeGuide help you say it more thoughtfully.</p>
                <span data-testid="say-it-better-cta" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#E7CF8f]">Try Say It Better <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" /></span>
              </div>
            </button>

            <p className="mt-8 font-serif-display text-lg sm:text-xl text-[#E7CF8f]">Scripture can point you in the right direction. LifeGuide helps you think through what comes next.</p>

            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
              <button data-testid="home-lifeguide-btn" onClick={lifeguide} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#C9A44C] text-[#0F172A] font-semibold hover:bg-[#d8b45c] transition-colors">See What LifeGuide Can Do <ArrowRight className="w-4 h-4" /></button>
              <button data-testid="home-lifeguide-link" onClick={lifeguide} className="text-sm text-white/80 underline underline-offset-4 hover:text-white transition-colors">Try LifeGuide</button>
              <span className="text-sm font-semibold text-white/90 inline-flex items-center gap-1.5"><BookOpen className="w-4 h-4 text-[#E7CF8f]" /> I Need a Proverb is always free.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Coming soon: additional AI-powered tools (Say It Better is already available today) */}
      <section data-testid="coming-soon-section" className="max-w-4xl mx-auto px-5 sm:px-8 pb-4 pt-2">
        <div className="rounded-3xl border border-[#E7DFC9] bg-[#FBF7EE] px-7 sm:px-10 py-9">
          <p className="text-xs uppercase tracking-[0.2em] text-[#B8912F] font-semibold mb-2">More helpful tools on the way</p>
          <h3 className="font-serif-display text-2xl sm:text-3xl text-[#0F172A]">The experience keeps getting better.</h3>
          <p className="mt-3 text-[#475569] leading-relaxed max-w-2xl">
            <strong className="text-[#0F172A]">Say It Better is available now</strong> — and it's just the beginning. Soon you'll have even more gentle, AI-powered ways to move from the right Scripture to your real next step:
          </p>
          <div className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
            {[
              "More conversational guidance",
              "Optional voice-based interaction",
              "More personalized follow-up",
              "Easier next-step guidance",
              "Smoother continuity between I Need a Proverb and LifeGuide",
            ].map((t) => (
              <div key={t} className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#B8912F] shrink-0" />
                <span className="text-sm text-[#475569] leading-relaxed">{t}<span className="ml-2 align-middle text-[10px] uppercase tracking-wide text-[#B8912F] font-semibold">Coming soon</span></span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm font-semibold text-[#2C4C3B]">I Need a Proverb stays free.</p>
        </div>
      </section>

      {/* Help Share the Wisdom — physical printed sharing cards */}
      <section data-testid="share-cards-section" className="max-w-6xl lg:max-w-[1180px] mx-auto px-5 sm:px-8 pb-6 pt-2">
        <div className="rounded-3xl border border-[#E7DFC9] bg-white px-6 sm:px-10 py-9 sm:py-11">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Product visual — first on mobile, left on desktop */}
            <div className="order-1">
              <CardShowcase />
            </div>
            {/* Copy + order */}
            <div className="order-2">
              <p className="text-xs uppercase tracking-[0.2em] text-[#B8912F] font-semibold mb-3">Help share the wisdom</p>
              <h3 className="font-serif-display text-3xl sm:text-4xl leading-[1.1] text-[#0F172A]">Put a Little Wisdom Into Someone's Hands</h3>
              <p className="mt-4 text-[#475569] leading-relaxed">
                Keep I Need a Proverb sharing cards with you and give one to someone who could use encouragement, direction, or Biblical wisdom. They can scan the QR code or visit INeedAProverb.com and find Scripture for what they're facing — free to use.
              </p>
              <p className="mt-5 text-sm font-semibold text-[#0F172A]">Professionally printed sharing cards · premium glossy finish</p>
              <div className="mt-3 flex items-baseline gap-2 text-[#0F172A]">
                <span className="text-sm font-semibold text-[#2C4C3B] uppercase tracking-wide">Starting at</span>
                <span className="font-serif-display text-3xl">$34.95</span>
                <span className="text-sm font-semibold text-[#2C4C3B] uppercase tracking-wide">Delivered</span>
              </div>
              <button data-testid="order-cards-btn" onClick={() => setCardModal(true)}
                className="mt-6 w-full sm:w-auto rounded-full bg-[#2C4C3B] text-white px-7 py-3.5 font-semibold hover:bg-[#24402F] transition-colors">
                Order Sharing Cards
              </button>
              <p className="mt-3 text-xs text-[#94A3B8]">Shipping included. I Need a Proverb remains free to use.</p>

              {/* Larger / custom orders */}
              <div className="mt-5 rounded-2xl border border-[#E7DFC9] bg-[#FBF7EE] px-5 py-4">
                <p className="text-sm font-semibold text-[#0F172A]">Need a larger quantity or custom order?</p>
                <a data-testid="larger-order-btn" target="_top" href="mailto:wisdom@ineedaproverb.com?subject=Larger%20Sharing%20Card%20Order&body=Hi%2C%20I%27m%20interested%20in%20ordering%20a%20larger%20quantity%20of%20I%20Need%20a%20Proverb%20Sharing%20Cards.%20Please%20contact%20me%20with%20options%20and%20pricing."
                  className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#2C4C3B] text-[#2C4C3B] px-5 py-2.5 text-sm font-semibold hover:bg-[#F1F5F1] transition-colors">
                  Ask About Larger Card Orders
                </a>
              </div>

              <p className="mt-4 text-sm text-[#475569]">
                Questions about Sharing Cards? Email <a data-testid="cards-email-link" target="_top" href="mailto:wisdom@ineedaproverb.com?subject=Sharing%20Cards%20Question" className="font-semibold text-[#2C4C3B] underline underline-offset-2 hover:text-[#1E3629]">wisdom@ineedaproverb.com</a>
              </p>
            </div>
          </div>
        </div>
        {cardModal && (
          <div data-testid="card-qty-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setCardModal(false)}>
            <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-1">
                <h4 className="font-serif-display text-2xl text-[#0F172A]">Choose your quantity</h4>
                <button data-testid="card-qty-modal-close" onClick={() => setCardModal(false)} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-sm text-[#94A3B8] mb-4">All quantities delivered. Same approved two-sided design.</p>
              <div className="space-y-2">
                {[[100, "$34.95"], [200, "$49.95"], [500, "$79.95"]].map(([q, price]) => (
                  <button key={q} data-testid={`modal-qty-${q}`} onClick={() => nav(`/cards?qty=${q}`)}
                    className="w-full flex items-center justify-between gap-4 rounded-xl border border-[#E2E8F0] px-4 py-3.5 hover:border-[#2C4C3B] hover:bg-[#FBF7EE] transition-colors text-left group">
                    <span className="text-sm font-semibold text-[#0F172A]">{q.toLocaleString()} Cards</span>
                    <span className="text-sm text-[#2C4C3B] font-semibold inline-flex items-center gap-1">{price} delivered <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" /></span>
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between gap-4 rounded-xl bg-[#FBF7EE] px-4 py-3">
                <span className="text-sm font-medium text-[#475569]">1,000+ or custom-branded orders</span>
                <a data-testid="modal-qty-custom" target="_top" href="mailto:wisdom@ineedaproverb.com?subject=Custom%20or%20Bulk%20Sharing%20Card%20Order" className="text-xs font-semibold text-[#2C4C3B] underline underline-offset-2 hover:text-[#1E3629]">Contact Us</a>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Coming Soon: The Christian Marketplace */}
      <section data-testid="marketplace-section" className="max-w-5xl mx-auto px-5 sm:px-8 pb-10 pt-2">
        <div className="relative overflow-hidden rounded-3xl border border-[#E7DFC9] bg-gradient-to-br from-[#FBF7EE] to-[#F3ECDD] px-7 sm:px-12 py-10 sm:py-12">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#C9A44C]/10 blur-3xl" aria-hidden />
          <div className="relative">
            <div className="flex items-center gap-3 mb-5">
              <span data-testid="marketplace-badge" className="inline-block text-[10px] font-semibold uppercase tracking-wide text-[#B8912F] bg-white border border-[#E7DFC9] rounded-full px-3 py-1">Coming Soon</span>
            </div>
            <div className="grid sm:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <h3 className="font-serif-display text-3xl sm:text-4xl leading-[1.1] text-[#0F172A]">Coming Soon: The Christian Marketplace</h3>
                <p className="mt-4 text-[#475569] leading-relaxed max-w-xl">
                  Shop curated Christian gifts, devotionals, books, Scripture-inspired products, and meaningful resources — all in one place.
                </p>
                <p className="mt-2 text-[#475569] leading-relaxed max-w-xl">
                  Thoughtful items to encourage faith, share wisdom, and bless others.
                </p>
                <p className="mt-5 text-sm text-[#475569]">
                  Questions or product suggestions? Email <a data-testid="marketplace-email-link" target="_top" href="mailto:wisdom@ineedaproverb.com?subject=Christian%20Marketplace" className="font-semibold text-[#2C4C3B] underline underline-offset-2 hover:text-[#1E3629]">wisdom@ineedaproverb.com</a>
                </p>
              </div>
              {/* Product-themed iconography */}
              <div className="grid grid-cols-2 gap-3 shrink-0 mx-auto sm:mx-0">
                {[Gift, BookOpen, Heart, Sparkles].map((Icon, i) => (
                  <span key={i} className="grid place-items-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white border border-[#E7DFC9] shadow-sm">
                    <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-[#2C4C3B]" strokeWidth={1.5} />
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* More than a verse of the day (closing credibility/positioning) */}
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C9A44C] font-semibold mb-3">Not just another daily verse</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl">More than a verse of the day.</h2>
          <p className="mt-4 text-white/70 leading-relaxed max-w-2xl mx-auto">
            With {provCount}+ verses from Proverbs, all 150 Psalms, additional biblical wisdom, 20+ real-life concerns, and needs like peace, courage, hope, direction and comfort, I Need a Proverb surfaces Scripture relevant to your moment.
          </p>
          <div className="mt-8 flex flex-wrap gap-2 justify-center">
            {CONCERN_WORDS.map((w) => <span key={w} className="px-4 py-2 rounded-full bg-white/10 border border-white/15 text-sm">{w}</span>)}
          </div>
          <div className="mt-3 flex flex-wrap gap-2 justify-center">
            {NEED_WORDS.map((w) => <span key={w} className="px-4 py-2 rounded-full bg-[#C9A44C]/15 border border-[#C9A44C]/30 text-sm text-[#E7CF8f]">{w}</span>)}
          </div>
        </div>
      </section>
    </div>
  );
}

function BenefitGroup({ title, items }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-[#C9A44C] font-semibold mb-3">{title}</p>
      <div className="space-y-3">
        {items.map((b) => (
          <div key={b} className="flex items-start gap-3">
            <span className="mt-0.5 grid place-items-center w-5 h-5 rounded-full bg-[#C9A44C]/20 shrink-0"><Check className="w-3.5 h-3.5 text-[#E7CF8f]" strokeWidth={2.5} /></span>
            <span className="text-sm text-white/85 leading-relaxed">{b}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Choice({ testid, icon: Icon, title, sub, onClick, primary }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`group text-left rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-0.5 ${primary ? "border-[#2C4C3B] bg-[#F1F5F1]" : "border-[#E2E8F0] bg-white hover:border-[#2C4C3B]/40"}`}>
      <div className={`grid place-items-center w-11 h-11 rounded-xl mb-4 ${primary ? "bg-[#2C4C3B] text-white" : "bg-[#F4F1EA] text-[#2C4C3B]"}`}>
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <h3 className="font-serif-display text-xl text-[#0F172A]">{title}</h3>
      <p className="text-sm text-[#475569] mt-1 leading-relaxed">{sub}</p>
      <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#2C4C3B] mt-3">Start <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" /></span>
    </button>
  );
}
function Stat({ value, label }) {
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white px-3 py-4">
      <div className="font-serif-display text-2xl sm:text-3xl text-[#0F172A]">{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-[#94A3B8] mt-1 leading-tight">{label}</div>
    </div>
  );
}
